import { z } from 'zod'
import { oc } from '@orpc/contract'
import { isKnownTimeZone } from './list-bookable-providers.contract'

/**
 * Provider Choice — the patient portal's "Select time" step (docs/GLOSSARY.md, "Patient
 * booking"). Patient-only: the Bookable Slots of one
 * provider on one calendar day. The day is `date` in `timeZone` — the same zone the portal
 * handed `listBookableProviders`, so a count shown there is the number of slots returned
 * here. A practitioner who is not a clinician or fails State Match yields no slots.
 *
 * Flat slot blocks only — never FHIR resources. The browser still reads the chosen Slot
 * through Medplum to post to v1 book, exactly as today (R1-Q9).
 */

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/
/** FHIR `id` grammar — the handler interpolates it into search strings. */
const FHIR_ID = /^[A-Za-z0-9\-.]{1,64}$/

/** `YYYY-MM-DD` naming a day that exists — `2026-02-30` would otherwise roll into March. */
function isCalendarDate(date: string): boolean {
  if (!CALENDAR_DATE.test(date)) return false
  const parsed = new Date(`${date}T00:00:00.000Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(date)
}

export const getProviderDaySlotsInput = z.object({
  practitionerId: z.string().regex(FHIR_ID, { message: 'Expected a FHIR resource id' }),
  /** Calendar day, `YYYY-MM-DD`, in `timeZone`. */
  date: z.string().refine(isCalendarDate, { message: 'Expected a calendar date (YYYY-MM-DD)' }),
  /** IANA zone `date` is read in (e.g. `America/New_York`). */
  timeZone: z.string().refine(isKnownTimeZone, { message: 'Unknown IANA time zone' }),
})

export const getProviderDaySlots = oc
  .route({
    method: 'GET',
    path: '/v2/appointments/bookable-providers/{practitionerId}/slots',
  })
  .input(getProviderDaySlotsInput)
  .output(
    z.object({
      /** Ascending by `start`. */
      slots: z.array(
        z.object({
          slotId: z.string(),
          /** ISO-8601 instants. */
          start: z.string(),
          end: z.string(),
        }),
      ),
    }),
  )
